import { apiRequest } from "./apiClient";
import { createActivity, getActivityActor } from "./activityService";
import { bagliHatirlaticilariSil } from "./linkedReminderService";
import { emitRestDataChanged } from "./restEventBus";
import { trackEngagementDataAdded } from "./engagementService";
import { recordMeaningfulReviewAction } from "./appReviewService";

const enc = (v) => encodeURIComponent(String(v || ""));

export const alisverisleriGetirService = async (familyId) => {
  if (!familyId) return [];
  const data = await apiRequest(`/shopping/${enc(familyId)}`);
  return Array.isArray(data?.items) ? data.items : [];
};

export const urunEkleService = async ({
  familyId,
  urunAdi,
  urunMiktar,
  urunBirim,
  urunKategori,
  tamAd,
  profilAvatar = null,
  user,
  notificationMode = "single",
  bulkBatchId = null,
  bulkTotal = null,
  suppressActivity = false,
}) => {
  const bulkSource = notificationMode === "bulkCooking" ? "cooking" : "ocr";
  const isBulkAdd = notificationMode === "bulkOcr" || notificationMode === "bulkCooking";
  const payload = {
    ad: String(urunAdi || "").trim(),
    miktar: urunMiktar,
    birim: urunBirim,
    kategori: urunKategori,
    alindi: false,
    eklenmeTarihi: new Date().toISOString(),
    ekleyen: tamAd,
    ekleyenUid: user?.uid,
  };

  const data = await apiRequest(`/shopping/${enc(familyId)}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const item = data?.item || payload;
  const id = item?.id || item?.firebaseId;

  trackEngagementDataAdded({ familyId, moduleKey: "shopping", itemId: id, metadata: { source: notificationMode } });

  emitRestDataChanged({
    familyId,
    resource: "shopping",
    action: "added",
    item,
    extra: isBulkAdd
      ? { silentNotification: true, bulkBatchId, bulkTotal, source: bulkSource }
      : undefined,
  });

  if (!suppressActivity) {
    await createActivity({
      familyId,
      type: "shopping_item_added",
      category: "shopping",
      actor: getActivityActor({ user, name: tamAd, avatar: profilAvatar }),
      payload: { itemName: payload.ad, amount: urunMiktar, unit: urunBirim, category: urunKategori },
      targetPage: "alisveris",
      targetId: id,
    });
  }

  return id;
};

export const urunleriTopluEkleService = async ({
  familyId,
  urunler = null,
  urunAdlari = [],
  urunKategori,
  tamAd,
  profilAvatar = null,
  user,
  source = "ocr",
}) => {
  const rawItems = Array.isArray(urunler) && urunler.length ? urunler : urunAdlari;
  const items = (rawItems || [])
    .map((x) => {
      if (x && typeof x === "object") {
        return {
          ad: String(x.ad || x.name || x.urunAdi || "").trim(),
          miktar: String(x.miktar || x.quantity || "1").trim() || "1",
          birim: String(x.birim || x.unit || "adet").trim() || "adet",
          kategori: String(x.kategori || x.category || urunKategori || "Market").trim() || "Market",
        };
      }
      return {
        ad: String(x || "").trim(),
        miktar: "1",
        birim: "adet",
        kategori: String(urunKategori || "Market").trim() || "Market",
      };
    })
    .filter((x) => x.ad)
    .slice(0, 60);

  if (!familyId || !user || items.length === 0) return { count: 0, ids: [] };

  const bulkSource = source === "cooking" ? "cooking" : "ocr";
  const notificationMode = bulkSource === "cooking" ? "bulkCooking" : "bulkOcr";
  const bulkBatchId = `shopping_${bulkSource}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const ids = [];
  for (const item of items) {
    try {
      const id = await urunEkleService({
        familyId,
        urunAdi: item.ad,
        urunMiktar: item.miktar,
        urunBirim: item.birim,
        urunKategori: item.kategori,
        tamAd,
        profilAvatar,
        user,
        notificationMode,
        bulkBatchId,
        bulkTotal: items.length,
        suppressActivity: true,
      });
      if (id) ids.push(id);
    } catch {}
  }

  if (ids.length > 0) {
    emitRestDataChanged({
      familyId,
      resource: "shopping",
      action: "bulkAdded",
      extra: {
        source: bulkSource,
        bulkBatchId,
        count: ids.length,
        total: items.length,
        itemIds: ids,
        ekleyen: tamAd,
        ekleyenUid: user?.uid,
      },
    });

    await createActivity({
      familyId,
      type: "shopping_items_bulk_added",
      category: "shopping",
      actor: getActivityActor({ user, name: tamAd, avatar: profilAvatar }),
      payload: { count: ids.length, source: bulkSource },
      targetPage: "alisveris",
      targetId: bulkBatchId,
    }).catch(() => {});
  }

  return { count: ids.length, ids };
};

export const urunSilService = async ({ familyId, urunId, urun, user, tamAd, profilAvatar = null }) => {
  await bagliHatirlaticilariSil({
    familyId,
    kaynak: "shopping",
    kaynakId: urunId,
    reminderId: urun?.reminderId,
    reminderIds: urun?.reminderIds,
  });

  await apiRequest(`/shopping/${enc(familyId)}/${enc(urunId)}`, { method: "DELETE" });
  emitRestDataChanged({ familyId, resource: "shopping", action: "deleted", itemId: urunId });

  await createActivity({
    familyId,
    type: "shopping_item_deleted",
    category: "shopping",
    actor: getActivityActor({ user, name: tamAd, avatar: profilAvatar }),
    payload: { itemName: urun?.ad || "" },
    targetPage: "alisveris",
    targetId: urunId,
  });
};

export const alindiDegistirService = async ({ familyId, urun, user, tamAd, profilAvatar = null }) => {
  const yeniDurumAlindi = !urun.alindi;
  const data = await apiRequest(`/shopping/${enc(familyId)}/${enc(urun.id)}/toggle`, {
    method: "PATCH",
    body: JSON.stringify({
      alindi: yeniDurumAlindi,
      alanUid: yeniDurumAlindi ? user?.uid || "" : "",
      alanAd: yeniDurumAlindi ? tamAd || "" : "",
    }),
  });
  const item = data?.item || { ...urun, alindi: yeniDurumAlindi };
  emitRestDataChanged({ familyId, resource: "shopping", action: "updated", item });

  if (yeniDurumAlindi) {
    void recordMeaningfulReviewAction(user?.uid, {
      trigger: "shopping_completed",
      moduleKey: "shopping",
      itemId: urun?.id,
    }).catch(() => {});
    await createActivity({
      familyId,
      type: "shopping_item_completed",
      category: "shopping",
      actor: getActivityActor({ user, name: tamAd, avatar: profilAvatar }),
      payload: { itemName: urun?.ad || "" },
      targetPage: "alisveris",
      targetId: urun?.id || "",
    });
  }
};
